/**
 * body will be passed to new Workers code object by WebWorkerEnabler
 * function syntax is for import only
 * @message     sending FETCH_TIMINGS_FAILED        to indicate error
 * @message     sending FETCH_TIMINGS_ERROR         to indicate error
 * @message     recieving START_FETCH_TIMINGS       to start the process of fetching the timings feed
 * @message     recieving PAUSE_FETCH_TIMINGS       to stop the process of fetching the timings feed
 * @message     recieving RESUME_FETCH_TIMINGS      to clear and restart process of fetching the timings feed
 * @message     sending FETCH_TIMINGS_SUCCEEDED     to transfer timings feed data
 */
export default function TimingsWebWorker() {

    // setup some vars
    const setup = {
        timingsFeedUrl: null,
        timingsDelay: 1000,
        dataFields: [
            'timings', 
            'clientRequestTimestampMs',
            'clientResponseTimestampMs',
            'serverHeaderTimestampMs',
            'serverContentTimestampMs',
            'serverCalculationTimeMs',
            'serverAgeMS',
            'wasCacheHit',
            'expiresMs'
        ]
    }

    // store data
    let data = {
        current: {},
        previous: {}
    }
    setup.dataFields.forEach(key => {
        data.current[key] = null
        data.previous[key] = null
    });

    // refer handlers
    let handlers = {}

    // switch for pause timings recursion
    let isPaused = false

    // main
    function timings() {

        // reset timeouthandler
        handlers.timingsTimeout = null

        // request feed and remember clientRequestTimestampMs
        const clientRequestTimestampMs = Date.now()
        fetch(new Request(setup.timingsFeedUrl), {cache: "no-store"}).then(function(response) {

            // validate response
            if(!response.ok) { throw Error(response.statusText) }
            
            // resolve json promise
            return new self.Promise((resolve) => {
                response.json().then(jsonBody => {
                    return resolve({ json: jsonBody, headers: response.headers })
                })
            }).catch(function(error) { throw Error(error) })

        }).then(function(responseObject) {

            // update previous data and reset current data
            data.previous = self.JSON.parse(self.JSON.stringify(data.current))
            setup.dataFields.forEach(key => { data.current[key] = null });

            // update current timings data
            data.current.timings = responseObject.json

            // update current timestamps data
            data.current.clientRequestTimestampMs = clientRequestTimestampMs
            data.current.clientResponseTimestampMs = Date.now()
            data.current.serverHeaderTimestampMs = Math.floor(Date.parse(responseObject.headers.get('date'))/1000)*1000 || 0
            data.current.serverContentTimestampMs = parseInt(data.current.timings.server_datetime_ms, 10)
            data.current.serverCalculationTimeMs = data.current.timings.calculation_time_ms
            data.current.serverAgeMS = responseObject.headers.get('age') || 0
            data.current.wasCacheHit = responseObject.headers.get('x-proxy-cache') == 'HIT'
            data.current.expiresMs = new Date(responseObject.headers.get('expires')).getTime()

            // send current data message
            postMessage(self.JSON.stringify({ 
                name: 'FETCH_TIMINGS_SUCCEEDED', 
                data: {
                    timings: data.current.timings,
                    timestamps: {
                        clientRequestTimestampMs: data.current.clientRequestTimestampMs,
                        clientResponseTimestampMs: data.current.clientResponseTimestampMs,
                        serverHeaderTimestampMs: data.current.serverHeaderTimestampMs,
                        serverContentTimestampMs: data.current.serverContentTimestampMs,
                        serverCalculationTimeMs: data.current.serverCalculationTimeMs,
                        serverAgeMS: data.current.serverAgeMS,
                        wasCacheHit: data.current.wasCacheHit,
                        expiresMs: data.current.expiresMs
                    }
                }
            }))

            // calculate latency
            let latency = data.previous.clientResponseTimestampMs - data.previous.clientRequestTimestampMs
            if(!data.previous.wasCacheHit) {
                latency -= data.previous.serverCalculationTimeMs
            }
            latency /= 2 // approximation

            // calculate current servertime
            const currentServerTime = data.previous.serverContentTimestampMs-data.previous.serverAgeMS-latency+Date.now()-data.previous.clientResponseTimestampMs

            // calculate the nearest next phasechange of any channel but expirestime at least
            let nextPhaseChangeTime = data.current.timings.channels.reduce(function(accumulator, item) {
                return Math.min(accumulator, item.active_phase_end_datetime)
            }, Infinity)  
            nextPhaseChangeTime = Math.max(nextPhaseChangeTime*1000, data.current.expiresMs)
            if(!isPaused) {               
                // timeout next timings
                const delay = nextPhaseChangeTime-currentServerTime+setup.timingsDelay
                handlers.timingsTimeout = setTimeout(timings, Math.max(1000, delay))
            }
            // console.table(self.Object.assign({}, data, {delay: delay})) 

        }).catch(function(error) {
            // send failed message
            postMessage(self.JSON.stringify({name: 'FETCH_TIMINGS_FAILED'}))
        })
    }

    // message action
    self.onmessage = (event) => {
        const messageObject = JSON.parse(event.data)
        switch(messageObject.name) {
            // start action
            case 'START_FETCH_TIMINGS':
                setup.timingsFeedUrl = messageObject.data
                timings();
                break
            // restart action
            case 'RESUME_FETCH_TIMINGS':
                isPaused = false
                timings();
                break
            // stop action
            case 'PAUSE_FETCH_TIMINGS':
                isPaused = true
                break
            default:
                console.warn('no valid name for message to TimingsWebWorker')
        }
    }

    // error action
    self.onerror = (event) => {
        // send error message
        postMessage(self.JSON.stringify({ name: 'FETCH_TIMINGS_ERROR' }))
    }    

}
